import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectScriptInfoList, updateScriptLog } from './DataCollectionSlice';
import { Checkbox, Button } from "@blueprintjs/core";

/**
 * DataCollectionControlPanel
 *
 * A React component that provides a control panel for managing data collection.
 * It displays a list of scripts retrieved from Unity and allows the user
 * to toggle logging for each script via checkboxes. Additionally, "Select All" and 
 * "Deselect All" buttons are provided to toggle all checkboxes at once.
 *
 * Returns:
 * - A control panel UI with checkboxes and buttons, or a message indicating no data if the script list is empty.
 */
const DataCollectionControlPanel = () => {
    const dispatch = useDispatch();
    const scriptInfoList = useSelector(selectScriptInfoList);

    if (!scriptInfoList) {
        return <div>No data received</div>;
    }

    const [checkboxState, setCheckboxState] = useState({});

    useEffect(() => {
        if (scriptInfoList) {
            const initialState = scriptInfoList.reduce((acc, scriptInfo) => {
                acc[scriptInfo.scriptAlias] = scriptInfo.logScript;
                return acc;
            }, {});
            setCheckboxState(initialState);
        }
    }, [scriptInfoList]);


    // change the script log value in the store and send the change to Unity
    const handleScriptCheckboxChange = (scriptAlias, logValue) => {
        setCheckboxState((prevState) => ({
            ...prevState,
            [scriptAlias]: logValue,
        }));

        dispatch(updateScriptLog({ scriptAlias, logValue }));
    };

    // Select all or deselect all checkboxes
    const handleSelectAll = (select) => {
        const newCheckboxState = { ...checkboxState };

        scriptInfoList.forEach((scriptInfo) => {
            if (scriptInfo.scriptAvailable) {
                newCheckboxState[scriptInfo.scriptAlias] = select;
                handleScriptCheckboxChange(scriptInfo.scriptAlias, select);
            }
        });
        setCheckboxState(newCheckboxState);
    };

    return (
        <div className="data-collection-panel">
            <h6>Script Logging Control</h6>
            {scriptInfoList.length > 0 ? (
                <div>
                    {scriptInfoList.map((scriptInfo, index) => (
                        <Checkbox
                            key={index}
                            label={scriptInfo.scriptAlias}
                            disabled={!scriptInfo.scriptAvailable}
                            checked={checkboxState[scriptInfo.scriptAlias] || false}
                            onChange={(e) => handleScriptCheckboxChange(scriptInfo.scriptAlias, e.target.checked)}
                        />
                    ))}
                    <div className="button-group">
                        <Button onClick={() => handleSelectAll(true)}>Select All</Button>
                        <Button onClick={() => handleSelectAll(false)}>Deselect All</Button>
                    </div>
                </div>
            ) : (
                <p>Loading script infos from Unity...</p>
            )}
        </div>
    );
};

export default DataCollectionControlPanel;